/**
 * app_lock_controller.js — device app lock (Face ID / Touch ID / passcode).
 *
 * Mounted once, app-wide, from base.html — the same shape as push-registration:
 * a hidden element whose controller consumes native capabilities the bridge
 * host registers. It is NOT a bridge component itself; it is the thing that
 * decides WHEN to lock, which is the half bridge--biometrics-lock deliberately
 * leaves to the web ("the web layer decides WHEN to lock; iOS owns the actual
 * biometric check" — BiometricsLockComponent.swift).
 *
 * Two capabilities, both already mounted in components/bridge_host.html:
 *   bridge--app-lifecycle  pushes "background"/"active" with awayMs. A WebView
 *                          inside a backgrounded native app does NOT reliably
 *                          fire visibilitychange, so this is the only trustworthy
 *                          signal that the app actually left the screen.
 *   bridge--biometrics-lock runs LAContext with .deviceOwnerAuthentication —
 *                          biometrics, falling back to the device passcode.
 *
 * WHEN IT LOCKS
 *   * On the first connect of a launch, so a cold start is covered. The flag is
 *     module-scoped, not instance state: Turbo replaces <body> on every visit so
 *     this controller connect/disconnects constantly, and instance state would
 *     re-lock the app on every navigation. It is ALSO mirrored to localStorage,
 *     because a native sheet is a second WKWebView with its own JS context and a
 *     module flag does not reach it — see UNLOCK_KEY below.
 *   * On "background", painting the cover BEFORE the app leaves the screen, so
 *     returning never flashes the previous page.
 *   * On "active" the cover only demands a re-auth if the app was away longer
 *     than GRACE_MS. Without that, opening the share sheet or the camera and
 *     coming straight back would fire a Face ID prompt every time.
 *
 * WHY localStorage AND NOT bridge--secure-storage: this is a preference, not a
 * credential. secure_storage_controller.js is explicit that plaintext web storage
 * is "fine for UI preferences and wrong for" credentials, and its PWA impl
 * rejects outright. The threat this lock answers is a lent or shoulder-surfed
 * phone, not an attacker with the app sandbox open. Same reasoning and same
 * storage as the maps-app preference in static/js/maps-navigation.js.
 *
 * THE ESCAPE HATCH IS LOAD-BEARING. canEvaluatePolicy fails outright on a device
 * with no passcode enrolled, replying { success: false, error: "unavailable" }.
 * Without a way past the cover, a user who enabled the toggle and later removed
 * their passcode would be locked out of their own account with no recourse and
 * no way to reach the setting that caused it. "Sign out" is always present.
 *
 * <!-- CSS: static/css/app-lock.css -->
 */
import { Controller } from "@hotwired/stimulus";

/** Device-local, per-install. Mirrors maps-navigation.js's key convention. */
export const PREF_KEY = "chippie_app_lock";

/**
 * How long the app may be away before returning demands a re-auth.
 *
 * The picker sheets iOS presents over a WebView (share, camera, files) do not
 * background the app, but the Maps handoff and a notification-tap round trip do.
 * A short grace keeps those from feeling like a challenge without meaningfully
 * widening the window a lent phone is exposed for.
 */
const GRACE_MS = 15000;

/**
 * Shared unlock state.
 *
 * WHY THIS IS NOT JUST A MODULE VARIABLE: a native sheet is a SECOND WKWebView
 * (bridge/sheet_controller.js -> UISheetPresentationController wrapping its own
 * web view), which loads a standalone form page and therefore boots a fresh JS
 * context. A module flag is invisible across that boundary, so opening Edit
 * Client from an invoice re-prompted for Face ID even though the main web view
 * was already unlocked. localStorage DOES cross it — that is how isLockEnabled()
 * already reads the same preference in both web views — so the unlocked state
 * has to live in the same place the enabled state does.
 *
 * The module flag stays as the fast path and as the fallback when storage
 * throws (private mode), which is also why setUnlocked() writes both.
 */
const UNLOCK_KEY = "chippie_app_lock_unlocked";

/**
 * Survives the connect/disconnect churn Turbo causes, so "lock once per launch"
 * means per launch and not per navigation. Reset on every background transition.
 */
let unlockedThisLaunch = false;

/** Unlocked in THIS web view, or in a sibling one since the last background. */
function isUnlocked() {
    if (unlockedThisLaunch) return true;
    try {
        return localStorage.getItem(UNLOCK_KEY) === "1";
    } catch {
        return false;
    }
}

/**
 * No TTL by design: the flag is cleared on the background transition, which is
 * the same moment the old module flag was reset, so the lock still demands a
 * re-auth exactly when it did before. The residual gap is a crash or force-quit
 * that never delivers "background", after which a cold launch would not lock.
 */
function setUnlocked(on) {
    unlockedThisLaunch = on;
    try {
        if (on) {
            localStorage.setItem(UNLOCK_KEY, "1");
        } else {
            localStorage.removeItem(UNLOCK_KEY);
        }
    } catch {
        /* storage unavailable; the module flag above still covers this web view */
    }
}

/** True only inside the iOS/Android shells; base.html stamps the attribute. */
export function isLockSupported() {
    return document.documentElement.hasAttribute("data-hotwire-native");
}

export function isLockEnabled() {
    try {
        return localStorage.getItem(PREF_KEY) === "on";
    } catch {
        // Private mode / storage disabled: fail OPEN. A lock the user cannot
        // turn off from the settings screen is worse than no lock.
        return false;
    }
}

export function setLockEnabled(on) {
    try {
        if (on) {
            localStorage.setItem(PREF_KEY, "on");
        } else {
            localStorage.removeItem(PREF_KEY);
        }
    } catch {
        /* storage unavailable; the toggle simply does not persist */
    }
}

export default class extends Controller {
    /**
     * sheet: this page is a native sheet target (templates/native/form_page.html
     * sets it). A sheet is a second web view presented OVER the already-unlocked
     * main one by an explicit tap inside it, so its own cold-start lock would
     * challenge a session the user unlocked seconds ago. Server-driven on purpose
     * — the page itself knows how it is being presented, and that answer does not
     * depend on any state surviving the trip between two web views.
     */
    static values = { sheet: Boolean };

    connect() {
        if (!isLockSupported()) return;

        this._onLifecycle = this.handleLifecycle.bind(this);
        document.addEventListener("chippie:app-lifecycle", this._onLifecycle);

        // The lifecycle listener above stays wired either way, so backgrounding
        // with a sheet open still covers it. Only the cold-start lock is skipped.
        if (this.sheetValue) return;

        // Cold launch (or a body swap while still locked): cover first, then ask.
        if (isLockEnabled() && !isUnlocked()) {
            this.lock();
        }
    }

    disconnect() {
        if (this._onLifecycle) {
            document.removeEventListener("chippie:app-lifecycle", this._onLifecycle);
            this._onLifecycle = null;
        }
        // The overlay lives on <body>, so Turbo's swap takes it with the page.
        // Nothing to tear down here: unlockedThisLaunch is still false, so the
        // next connect() re-covers before the new page can be seen.
        this._overlay = null;
        this._prompting = false;
    }

    handleLifecycle(event) {
        const detail = (event && event.detail) || {};
        if (!isLockEnabled()) return;

        if (detail.state === "background") {
            // Cover on the way out, not on the way back in — a cover painted
            // after the app returns is a cover the user has already seen past.
            setUnlocked(false);
            this.showOverlay();
            return;
        }

        if (detail.state === "active" && this._overlay) {
            if (Number(detail.awayMs) < GRACE_MS) {
                this.unlocked();
            } else {
                this.attemptUnlock();
            }
        }
    }

    /** Cover the app and immediately ask for the re-auth. */
    lock() {
        this.showOverlay();
        this.attemptUnlock();
    }

    async attemptUnlock() {
        // The bridge host mounts in the same <body> as this controller, so on a
        // cold connect the facade may not have registered yet. Re-entrancy guard
        // doubles as the "one system prompt at a time" guard.
        if (this._prompting) return;
        const bridge = window.ChippieBridge;
        if (!bridge || typeof bridge.biometricsLock !== "function") {
            this.showError("Unlock is not available yet. Try again.");
            return;
        }

        this._prompting = true;
        this.showError("");
        try {
            const ok = await bridge.biometricsLock({ reason: "Unlock Chippie" });
            if (ok) {
                this.unlocked();
            } else {
                // Covers a cancelled prompt AND a device that cannot evaluate the
                // policy at all. Both leave the cover up with a retry; the sign-out
                // link below it is the only way out of the second case.
                this.showError("Couldn't verify it's you.");
            }
        } catch {
            this.showError("Couldn't verify it's you.");
        } finally {
            this._prompting = false;
        }
    }

    unlocked() {
        setUnlocked(true);
        if (this._overlay) {
            this._overlay.remove();
            this._overlay = null;
        }
    }

    /** Idempotent: a second call while covered must not stack a second cover. */
    showOverlay() {
        const existing = document.getElementById("app-lock-overlay");
        if (existing) {
            this._overlay = existing;
            return;
        }

        const overlay = document.createElement("div");
        overlay.id = "app-lock-overlay";
        overlay.className = "app-lock-overlay";
        overlay.setAttribute("role", "dialog");
        overlay.setAttribute("aria-modal", "true");
        overlay.setAttribute("aria-label", "Chippie is locked");
        overlay.innerHTML =
            '<i class="bi bi-lock-fill app-lock-overlay__icon" aria-hidden="true"></i>' +
            '<p class="app-lock-overlay__title">Chippie is locked</p>' +
            '<p class="app-lock-overlay__body">Unlock with Face ID, Touch ID or your device passcode to get back to your jobs.</p>' +
            '<p class="app-lock-overlay__error" hidden></p>' +
            '<button type="button" class="btn btn-light app-lock-overlay__unlock">Unlock</button>' +
            '<button type="button" class="app-lock-overlay__signout">Sign out instead</button>';

        overlay.querySelector(".app-lock-overlay__unlock")
            .addEventListener("click", () => this.attemptUnlock());
        overlay.querySelector(".app-lock-overlay__signout")
            .addEventListener("click", () => {
                // GET is an accepted logout path on purpose — auth/views.py
                // documents it as the recovery route for exactly this shape of
                // stuck user, so the escape hatch needs no CSRF token.
                window.location.href = "/auth/logout";
            });

        document.body.appendChild(overlay);
        this._overlay = overlay;
    }

    showError(message) {
        if (!this._overlay) return;
        const el = this._overlay.querySelector(".app-lock-overlay__error");
        if (!el) return;
        el.textContent = message;
        el.hidden = !message;
    }
}
